class Heatmap {
  constructor(config) {
    this.container = config.container;
    this.data = config.data || [];
    this.scale = config.scale;
    this.xAxis = config.xAxis || {};
    this.yAxis = config.yAxis || {};
    this.legend = config.legend;
    this.events = config.events || {};
    this.init();
  }

  configureEvents() {
    this.events.mouseover = this.events.mouseover || function () {};
    this.events.mouseout = this.events.mouseout || function () {};
    this.events.click = this.events.click || function () {};
    return this;
  }

  /**
   * This method is used to set data for the Heatmap
   * @param {array} data [data for heatmap]
   */
  setData(data) {
    this.data = data;
    return this;
  }

  /**
   * This method is used to set the chart dimensions
   * @param {string} selector  [CSS selector for chart]
   * @param {object} [margin] [margins to apply]
   * @return {object} [chart dimensions]
   */
  setChartDimensions(selector = this.container, margin = { top: 35, right: 15, bottom: 25, left: 50 }) {
    let width = d3.select(selector).style('width');
    width = parseInt(width.substring(0, width.length - 2)) * 0.95;

    let height = width * 0.35;

    // Subtract margins from width and height
    (width = width - margin.left - margin.right), (height = height - margin.top - margin.bottom);

    let xAxisKeys = [...new Set(this.data.map(d => d.rdate))];
    let gridSize = {};
    gridSize.cellWidth = this.xAxis.minCellSize || Math.floor(width / xAxisKeys.length);
    gridSize.cellHeight = this.yAxis.minCellSize || Math.floor(width / xAxisKeys.length);

    if (gridSize.cellWidth > 40) {
      gridSize.cellWidth = 40;
    }

    if (gridSize.cellHeight > 40) {
      gridSize.cellHeight = 40;
    }

    const chartDimensions = {
      margin: margin,
      width: width,
      height: height,
      gridSize: gridSize,
    };
    this.chartDimensions = chartDimensions;
    return chartDimensions;
  }

  /**
   * This method is used to hide an existing Heatmap tooltip
   * @return {object} [returns Heatmap]
   */
  hideTooltip(cardTooltip) {
    cardTooltip.style('opacity', 0);
    return this;
  }

  /**
   * This method is used to remove an existing Heatmap tooltip
   * @return {object} [returns Heatmap]
   */
  removeTooltip() {
    if (typeof this.tooltipHash !== 'undefined' && document.querySelector(`#${this.tooltipHash}`) !== null) {
      document.querySelector(`#${this.tooltipHash}`).remove();
    }
  }

  /**
   * This method is used to remove an existing Heatmap
   * @return {object} [returns Heatmap]
   */
  removeSVG(message = '') {
    document.querySelector(this.container).innerHTML = message;
    this.removeTooltip();
    return this;
  }

  /**
   * This method is used to create the SVG that all of the other chart elements will be appended to
   * @return {object} [returns Heatmap]
   */
  createSVG() {
    const chartDimensions = this.setChartDimensions();
    const { width, height, margin } = chartDimensions;

    this.removeSVG();

    const svg = d3
      .select(this.container)
      .append('svg')
      .attr('width', chartDimensions.width + margin.left + margin.right)
      .attr('height', height + 20)
      .attr('id', 'd3-graph')
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    this.svg = svg;
    return this;
  } // createSVG

  /**
   * This method is used to create a dynamic color scale i.e. data driven
   * @param  {number} value [individual result value]
   * @return {string}       [returns HEX code that corresponds to result value]
   */
  createDynamicScale(value) {
    const extent = d3.extent(this.data, d => d.rvalue);
    const colorScale = d3.scaleLinear().domain(extent).range([1, 0.35]);

    const breakVal = parseFloat((extent[1] - extent[0]) / 6);
    let breaks = [extent[0]];
    for (let i = 0; i < 5; i++) {
      breaks.push(extent[0] + breakVal + breakVal * i);
    }
    breaks.push(extent[1]);
    let colors = breaks.map(d => d3.interpolateSpectral(colorScale(d)));
    this.scale = { breaks, colors };
    return d3.interpolateSpectral(colorScale(value));
  } // createDynamicScale

  /**
   * This method is used to create a static color scale i.e. LRE defined
   * @param  {number/string} parameter [active parameter]
   * @param  {number} value     [individual result value]
   * @return {string}           [returns HEX code that corresponds to result value]
   */
  createStaticScale(parameter, value) {
    const extent = d3.extent(this.data, d => d.rvalue);
    if (parameter == '8' && value >= this.scale.breaks[this.scale.breaks.length - 1]) {
      return this.scale.colors[this.scale.colors.length - 1];
    }
    const colorScale = d3.scaleLinear().domain(this.scale.breaks).range(this.scale.colors);
    return colorScale(value);
  } // createStaticScale

  /**
   * This method is a utility function for creating a static scale
   * Using defined class breaks, the method inserts a value into an array of class breaks
   * and then sorts the array and finds the index in the array of the inserted value
   * The inserted value is then removed from the array.
   * If the index is the first value in the array, we add 1
   * If the index is the last value in the array, we subtract 1
   * The index is then used to return the hex code from the corresponding class assignment using the index
   * @param  {[type]} breaks [description]
   * @param  {[type]} colors [description]
   * @param  {[type]} value  [description]
   * @return {[type]}        [description]
   */
  calcColorScaleVal(breaks, colors, value) {
    // Insert value into array and sort it
    let breaksArr = breaks.slice(0);
    breaksArr.push(+value);
    breaksArr.sort((a, b) => b - a);

    // Find index of inserted value and then remove inserted value from array
    let index = breaksArr.indexOf(+value);
    breaksArr.splice(index, 1);

    // If val is first in array, add 1
    // If val is last in array, subtract 1
    if (index === 0) {
      index = index + 1;
    } else if (index === breaks.length) {
      index = index - 1;
    }

    // Return the Hex code at the specified index
    return colors[index];
  } // calcColorScaleVal

  /**
   * This method is used to create the heatmap color scale
   * Depending on the active parameter it will either use a dynamic or static color scale
   * @param  {number/string} parameter [active parameter]
   * @param  {number} value     [individual result value]
   * @return {string}           [returns HEX code that corresponds to result value]
   */
  createScale(parameter, value) {
    if (
      parameter == '3' ||
      parameter == '8' ||
      parameter == '7' ||
      parameter == '100138' ||
      parameter == '100139' ||
      parameter == '100140'
    ) {
      //      if ( parameter == "3" || parameter == "8" || parameter == "7") {
      return this.createStaticScale(parameter, value);
    } else {
      return this.createDynamicScale(value);
    }
  } // createScale

  /**
   * This method is used to create a x or y axis for the heatmap
   * @param  {string}  type                [axis type i.e. X or Y]
   * @param  {string} axisField           [Field that to be used for axis labels]
   * @param  {String}  [axisClass='label'] [css class(es) to apply to axis]
   * @return {object}                     [returns Heatmap]
   */
  createAxis(type, axisField, axisClass = 'label') {
    const axisKeys = [...new Set(this.data.map(d => d[axisField]))];
    const { gridSize } = this.chartDimensions;
    const { formatDate } = util;

    if (type === 'x') {
      this.xAxisKeys = axisKeys;
      const axis = this.svg.selectAll(axisClass).data(axisKeys);

      if (this.xAxis.enabled) {
        const axisFormat = this.xAxis.format || 'mm/dd/yyyy';
        axis
          .enter()
          .append('text')
          .text((d, i) => {
            const alternateLabelEvery = this.xAxis.alternateLabelEvery || 1;
            if (i === 0 || i % alternateLabelEvery === 0) {
              if (this.xAxis.format === 'yyyy-mm-dd hh:mm:ss') {
                return d;
              } else {
                return formatDate(d, 'mm/dd/yyyy');
              }
            }
          })
          .attr('class', axisClass)
          .attr('x', 0)
            .style('font-size', '18px')
          .attr('y', (d, i) => i * gridSize.cellWidth)
          .attr('transform', `translate(${gridSize.cellWidth / 2},-6) rotate(-90)`)
          .style('text-anchor', 'left');
      }
    } else if (type === 'y') {
      this.yAxisKeys = axisKeys.sort((a, b) => +a - +b);
      const axis = this.svg.selectAll(axisClass).data(axisKeys);
      axis
        .enter()
        .append('text')
        .text(d => `${d} m`)
        .attr('class', axisClass)
          .style('font-size', '18px')
        .attr('x', 0)
        .attr('y', (d, i) => i * gridSize.cellHeight)
        .attr('transform', `translate(-6, ${gridSize.cellHeight / 2})`)
        .style('text-anchor', 'end');
    }
    return this;
  }

  /**
   * This method is used to create tooltip for the heatmap
   * @return {object} [returns the tooltip]
   */
  initTooltip() {
    const hash = Math.floor(Math.random() * 99999);
    this.tooltipHash = `card-tooltip-${hash}`;
    const cardTooltip = d3
      .select('body')
      .append('div')
      .attr('id', `card-tooltip-${hash}`)
      .attr('class', 'card-tooltip')
      .style('opacity', 0);
    return cardTooltip;
  }

  createTooltipHTML(d) {
    let depthValue = d.depth_value;
    if (depthValue == 9999) {
      depthValue = 'Bottom';
    } else {
      depthValue = `${depthValue} meters`;
    }
    return `
      <div class="tooltip-value">${d.rdate}</div>
      <div class="tooltip-value">${depthValue}</div>
      <div class="tooltip-value">${d.rvalue.toFixed(1)} ${d.unit_name}</div>
    `;
  }

  //removing and replacing with below 9/16
  //   createLegendText(breaks, value, i, parameter) {
  //     const index = breaks.indexOf(value);
  //     const lastIndex = breaks.length-1;
  //     const staticParameters = ["3", "8", "7", "100138", "100139", "100140"];
  // //    const staticParameters = ["3", "8", "7"];
  //     if ( i === 0 ) {
  //       return `≥ ${value}`;
  //     } else if ( i === lastIndex ) {
  //       return `≤ ${value}`;
  //     } else if ( staticParameters.includes(parameter) ) {
  //       return `${breaks[index - 1]} - ${value}`;
  //     }
  //   }


  formatNumber(num, decimalPlaces) {
    if (Math.floor(num) !== num) {
      // Format to the specified number of decimal places and then parse back to float
      return parseFloat(num.toFixed(decimalPlaces)).toString();
    } else {
      // If num is an integer, return it as is
      return num.toString();
    }
  }

  createLegendText(breaks, value, i, parameter) {
    const index = breaks.indexOf(value);
    const lastIndex = breaks.length - 1;
    const staticParameters = ['3', '8', '7', '100138', '100139', '100140'];
    //    const staticParameters = ["3", "8", "7"];
    if (i === 0) {
      return `${value} <`;
    } else if (i === lastIndex) {
      return `< ${value}`;
    }
    else if (!staticParameters.includes(parameter)) {
      return `${value.toFixed(0)}`;
    }
    else {
      return `${this.formatNumber(value, 2)}`;
    }
  }

  createLegend() {
    const { gridSize } = this.chartDimensions;
    const { breaks, colors } = this.scale;

    if (typeof this.legend === 'undefined') {
      this.legend = {
        itemWidth: 100,
      };
    }

    // Create an array of all cell positions and then sort ascending
    // This array will be used in the next step to get the largest y position
    // that will be used to set the legend position
    const cells = [].slice
      .call(document.querySelectorAll('rect.value'))
      .map(cell => parseInt(cell.getAttribute('y')))
      .sort((a, b) => b - a);

    // Grab the largest aka last y position to use to position the legend
    const lastYPosition = cells[0];

    const legend = this.svg.selectAll('.legend').data(breaks).enter().append('g').attr('class', 'legend');

    legend
      .append('rect')
      .attr('x', (d, i) => this.legend.itemWidth * i)
      .attr('y', lastYPosition + 60)
      .attr('width', this.legend.itemWidth)
      .attr('height', gridSize.cellHeight / 1.5)
      .style('fill', (d, i) => colors[i]);

    legend
      .append('text')
        .style('font-size', '18px')
      .attr('class', 'mono')
      .text((d, i) => this.createLegendText(breaks, d, i, this.data[0].parameterid))
      .attr('x', (d, i) => this.legend.itemWidth * i + 5)
      .attr('y', lastYPosition + 65 + gridSize.cellHeight);

    legend.exit().remove();
  }

  onMouseOver(d, cardTooltip) {
    this.events.mouseover(d);
    d3.selectAll(`${this.container} .row-label`).classed('axis-highlight', dd => d.depth_value === dd);
    d3.selectAll(`${this.container} .col-label`).classed('axis-highlight', dd => d.rdate === dd);
    cardTooltip.transition().duration(200).style('opacity', 0.8);
    this.createTooltipHTML(d, cardTooltip);
    cardTooltip
      .html(this.createTooltipHTML(d))
      .style('left', d3.event.pageX + 'px')
      .style('top', d3.event.pageY - 28 + 'px');
    return this;
  }

  onMouseOut(d, cardTooltip) {
    this.events.mouseout(d);
    d3.selectAll(`${self.container} .row-label`).classed('axis-highlight', false);
    d3.selectAll(`${self.container} .col-label`).classed('axis-highlight', false);
    cardTooltip.transition().duration(200).style('opacity', 0);
  }

  onClick(d, cardTooltip) {
    this.events.click(d);
  }

  /**
   * This method is used to create the heatmap cells
   * @return {object} [Return Heatmap]
   */
  createCards() {
    const { gridSize } = this.chartDimensions;
    const extent = d3.extent(this.data, d => d.rvalue);

    // create cells, joining data with each
    const cards = this.svg.selectAll('.value').data(this.data, d => `${d.depth_value} : ${d.rdate}`);

    const cardTooltip = this.initTooltip();

    // Append heatmap cards to SVG
    cards
      .enter()
      .append('rect')
      .attr('x', d => this.xAxisKeys.indexOf(d.rdate) * gridSize.cellWidth)
      .attr('y', d => this.yAxisKeys.indexOf(d.depth_value) * gridSize.cellHeight)
      .attr('class', 'value')
      .attr('width', gridSize.cellWidth)
      .attr('height', gridSize.cellHeight)
      .style('opacity', 0.95)
      .style('fill', d => this.createScale(d.parameterid, d.rvalue))
      .style('stroke', d => this.createScale(d.parameterid, d.rvalue))
      .on('mouseover', d => this.onMouseOver(d, cardTooltip))
      .on('mouseout', d => this.onMouseOut(d, cardTooltip))
      .on('click', d => this.onClick(d, cardTooltip));

    cards.exit().remove();
    this.createLegend();
    this.resize();
    return this;
  }

  /**
   * This method is used to resize the SVG after the heatmap cards have rendered
   * @return {object} [returns Heatmap]
   */
  resize() {
    const svg = document.querySelector(`${this.container} svg g`);
    const container = document.querySelector(this.container);
    const height = svg.getBBox().height;
    container.style.height = `${height - 50}px`;
    return this;
  }

  /**
   * This method is used to draw the heatmap
   * @return {object} [returns Heatmap]
   */
  draw() {
    this.createSVG().createAxis('x', 'rdate', 'col-label').createAxis('y', 'depth_value', 'row-label').createCards();
    return this;
  }

  init() {
    this.configureEvents();
  }
}
